<template>
  <UISideSheet
    :is-open="isPanelOpen"
    :title="panelTitle"
    :show-back="isViewingTopics"
    width="550px"
    full-height-mobile
    @toggle-side-sheet="tutorialsPanelStore.closePanel()"
    @back="selectedCategoryId = null"
    @closed="handleSheetClosed"
  >
    <div
      data-cy="tutorials-panel"
      class="tutorials-panel-content"
    >
      <TutorialsCategoryList
        v-if="!isViewingTopics"
        @select="selectCategory"
      />
      <TutorialsTopicList
        v-else
        :category-id="selectedCategoryId"
        @select-topic="handleTopicClick"
      />
    </div>
  </UISideSheet>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { onboardingCategories } from '@/constants/onboardingChecklist';
import { useTutorialsPanelStore } from '@/stores/useTutorialsPanelStore';

const tutorialsPanelStore = useTutorialsPanelStore();
const { isPanelOpen } = storeToRefs(tutorialsPanelStore);
const { canLaunchTopic, launchTopic } = useOnboardingTopicLaunch();
const { trackEvent, eventNames } = useAcvAnalytics();

const source = 'tutorials-panel';

// Two views: the category list, or (once a category is picked) that category's topics.
const selectedCategoryId = ref(null);
const isViewingTopics = computed(() => Boolean(selectedCategoryId.value));

const selectCategory = (categoryId) => {
  trackEvent(eventNames.GETSTARTED_CATEGORY_VIEWED, { category_id: categoryId, source });
  selectedCategoryId.value = categoryId;
};

const selectedCategory = computed(() =>
  onboardingCategories.find((category) => category.id === selectedCategoryId.value),
);
const panelTitle = computed(() => selectedCategory.value?.name ?? 'Tutorials');

// Always re-enter on the category view, never mid-drill-down from a previous visit.
watch(isPanelOpen, (open) => {
  if (!open) {
    selectedCategoryId.value = null;
  }
});

// Nothing launches until this panel is actually gone, whatever the topic's content type:
// a walkthrough has to measure targets that sit beneath the panel, and the static modal
// shares a z-index with it, so the panel would paint over it.
const pendingTopicId = ref(null);

const handleTopicClick = (topicId) => {
  if (!canLaunchTopic(topicId)) {
    return;
  }

  trackEvent(eventNames.GETSTARTED_TOPIC_STARTED, {
    topic_id: topicId,
    category_id: selectedCategoryId.value,
    source,
  });

  pendingTopicId.value = topicId;
  tutorialsPanelStore.closePanel();
  // Mobile only: MobileNavBar's slide-out menu sits underneath this panel and must also close
  tutorialsPanelStore.requestMobileMenuClose();
};

// `closed` fires on every close (X, overlay, back, a topic pick), not just ours — only
// act when something is actually queued.
const handleSheetClosed = () => {
  const topicId = pendingTopicId.value;
  pendingTopicId.value = null;

  if (topicId) {
    launchTopic(topicId, { source });
    return;
  }

  trackEvent(eventNames.GETSTARTED_DISMISSED, { dismiss_type: 'panel_closed', source });
};
</script>
